import {BoxedObjectEditor} from "./BoxedObjectEditor";
import {TextEditor} from "../../editors/TextEditor";
import {drawTextBlock, normalizeAngle} from "./CanvasUtils.ts";
import {ResizeHandler} from "./ResizeHandler";
import {NODE_REGISTRY} from "./NodeRegistry";

const HANDLER_WIDTH = 6;
const EDITOR_WIDTH = 200;

export const HOVER_COLOR = "#aedecf";

export class ProcessNodeEditor extends BoxedObjectEditor {
	constructor(parent, nodeModel, nodeCoords, resizable) {
		super(parent, true, resizable);
		this.node = nodeModel;
		this.coords = nodeCoords;
		this.textEditor = null;
		this.handlers = null;
	}

	get lineWidth() {
		return 2;
	}

	get bg() {
		return this.hovered ? HOVER_COLOR : S.BASE_COLORS[16];
	}

	get x() {
		return this.coords.x;
	}

	get y() {
		return this.coords.y;
	}

	set x(val) {
		this.coords.x = val;
	}

	set y(val) {
		this.coords.y = val;
	}

	get nameX() {
		return this.coords.x;
	}

	get nameY() {
		return this.coords.y;
	}

	get nameHAlign() {
		return "center";
	}

	get nameVAlign() {
		return "middle";
	}

	hit(x, y) {
		if (this.handlers) {
			// If selected check handlers first
			for (let h of this.handlers) {
				if (h.hit(x, y)) return h;
			}
		}
		return super.hit(x, y);
	}

	draw(ctx) {
		ctx.save();
		ctx.strokeStyle = "black";
		ctx.lineWidth = this.lineWidth;
		const rect = BoxedObjectEditor.strokeBox(this.x, this.y, this.coords.w, this.coords.h, this.lineWidth);
		ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);
		ctx.restore();
	}

	/** Called when node changed position by dragging */
	positionDragged(oldX, oldY, x, y) {
		if (this.dragSyncX) {
			for (let pt of this.dragSyncX) pt.x = x;
		}
		if (this.dragSyncY) {
			for (let pt of this.dragSyncY) pt.y = y;
		}
	}


	startDragging() {
		super.startDragging();
		/** @type {ProcessEditor} */
		const pe = this.findProcessEditor();
		const ins = pe.findIncommingConnections(this.node.id);
		const outs = pe.findOutgoingConnections(this.node.id);
		this.dragSyncX = [];
		this.dragSyncY = [];
		if (ins.length > 0) {
			for (let con of ins) {
				ProcessNodeEditor.filterMiddlePoints(pe, con, (p) => p.x === this.startX, this.dragSyncX);
				ProcessNodeEditor.filterMiddlePoints(pe, con, (p) => p.y === this.startY, this.dragSyncY);
			}
		}
		if (outs.length > 0) {
			for (let con of outs) {
				ProcessNodeEditor.filterMiddlePoints(pe, con, (p) => p.x === this.startX, this.dragSyncX);
				ProcessNodeEditor.filterMiddlePoints(pe, con, (p) => p.y === this.startY, this.dragSyncY);
			}
		}
	}


	/**
	 * @param {ProcessEditor} pe
	 * @param {PConnection} con
	 * @param {function} test
	 * @param {Point[]} points
	 */
	static filterMiddlePoints(pe, con, test, points) {
		let nodeCoords = pe.model.chart[con.from];
		if (nodeCoords.out) {
			const edgeCoords = nodeCoords.out[con.edge.id];
			if (edgeCoords && edgeCoords.mp) {
				for (let p of edgeCoords.mp) {
					if (test(p)) points.push(p);
				}
			}
		}
	}

	stopDragging(diffX, diffY, snapsX, snapsY) {
		super.stopDragging(diffX, diffY, snapsX, snapsY);
		delete this.dragSyncX;
		delete this.dragSyncY;
	}

	/**
	 * Default implementation place arrow tip on bounding rectangle
	 * @param {number} angle
	 * @return {{tipY: *, tipX: *}|{angle: *}}
	 */
	calcIncomingArrowTip(angle) {
		if (this.coords.w === 0 || this.coords.h === 0) return { tipX: this.coords.x, tipY: this.coords.y };
		const alpha = normalizeAngle(angle + Math.PI);	// outgoing angle
		const w = this.coords.w/2;
		const h = this.coords.h/2;
		const cornerAngle = Math.atan(h / w);

		const tip = { angle: angle };
		if (alpha > -cornerAngle && alpha <= cornerAngle) {
			tip.tipX = this.coords.x + w;
			tip.tipY = this.coords.y + Math.tan(alpha) * w;
		} else if (alpha > cornerAngle && alpha <= (Math.PI-cornerAngle)) {
			tip.tipX = this.coords.x + Math.tan(Math.PI/2 - alpha) * h;
			tip.tipY = this.coords.y + h;
		} else if (alpha < -cornerAngle && alpha >= (-Math.PI+cornerAngle)) {
			tip.tipX = this.coords.x - Math.tan(Math.PI/2 - alpha) * h;
			tip.tipY = this.coords.y - h;
		} else {
			tip.tipX = this.coords.x - w;
			tip.tipY = this.coords.y - Math.tan(alpha) * w;
		}
		return tip;
	}

	get selected() {
		return super.selected;
	}

	set selected(s) {
		super.selected = s;
		if (s && this.resizable) {
			this.handlers = [];
			this.handlers.push(new ResizeHandler(this, "left", "top"));
			this.handlers.push(new ResizeHandler(this, "center", "top"));
			this.handlers.push(new ResizeHandler(this, "right", "top"));
			this.handlers.push(new ResizeHandler(this, "left", "middle"));
			this.handlers.push(new ResizeHandler(this, "right", "middle"));
			this.handlers.push(new ResizeHandler(this, "left", "bottom"));
			this.handlers.push(new ResizeHandler(this, "center", "bottom"));
			this.handlers.push(new ResizeHandler(this, "right", "bottom"));
		} else this.handlers = null;
	}

	drawHandlers(ctx, pixelWidth = 1) {
		ctx.save();
		ctx.strokeStyle = S.HI_COLORS[8];
		ctx.lineWidth = pixelWidth;
		ctx.setLineDash([4 * pixelWidth, 4 * pixelWidth]);
		const rect = BoxedObjectEditor.strokeBox(this.x, this.y, this.coords.w, this.coords.h, pixelWidth);
		ctx.strokeRect(rect.left, rect.top, rect.width, rect.height);
		ctx.restore();

		if (this.handlers) {
			for (let h of this.handlers) {
				h.draw(ctx, pixelWidth);
			}
		}
	}

	drawNodeName(ctx, rect) {
		if (this.node.name) {
			ctx.save();
			ctx.fillStyle = "black";
			const max = Math.floor(rect.width * .9);
			drawTextBlock(ctx, this.node.name, this.nameX, this.nameY + 2, max,11, this.nameHAlign, this.nameVAlign);
			ctx.restore();
		}
	}

	calcBoundingBox() {
		return BoxedObjectEditor.boundingBox(this.coords.x, this.coords.y, this.coords.w, this.coords.h);
	}

	doubleClick(e, canvas, pos) {
		const domc = canvas.editor.domCanvas;
		const canvasRect = domc.getBoundingClientRect();	// screen position

		const ops = {};
		ops.style = "width:" + EDITOR_WIDTH + "px; position:fixed; left:" + (canvasRect.left + e.offsetX - EDITOR_WIDTH / 2) + "px;" +
			"top:" + (canvasRect.top + e.offsetY - 10) + "px; z-index:100000";
		this.textEditor = new TextEditor(null, this.node.id + ".name", this.node, "name", ops);
		$("body").append(this.textEditor.ui);
		this.textEditor.widget.on('keydown', (e)=>{
			if (e.keyCode === 13) {
				e.preventDefault();
				this.textEditor.root.remove();
				this.textEditor = null;
				canvas.editor.update();
			}
		});
		this.textEditor.focus();
	}

	canConnectFrom() {
		const mo = NODE_REGISTRY.get(this.node.class).maxOut;
		return mo === null || mo > 0;
	}

	canConnectTo(fromEditor) {
		const mi = NODE_REGISTRY.get(this.node.class).maxIn;
		return mi === null || mi > 0;
	}

	connectTo(target) {
		const pe = this.findProcessEditor();
		pe.newEdge(this.node.id, target.node.id);
	}

	/**
	 * @return {ProcessEditor}
	 */
	findProcessEditor() {
		let ed = this.parent;
		while (ed !== null) {
			if (ed.isProcessEditor) return ed;
			ed = ed.parent;
		}
		throw "Cannot find ProcessEditor";
	}

	/**
	 * Generate a name for newly created connection
	 * @param {PConnection[]} currentConnections
	 * @return {PEdge}
	 */
	generateOutgoingEdge(currentConnections) {
		let edgeId;
		if (ProcessNodeEditor.containsEdgeId(currentConnections, "THEN")) edgeId = ProcessNodeEditor.randomEdgeId(currentConnections, "edge-");
		else edgeId = "THEN";

		return {
			id: edgeId,
			"class": "com.anneca.intouch.workflow.edge.SimpleEdge"
		};
	}

	static randomEdgeId(connections, prefix) {
		let id;
		let idx = 1;
		do {
			id = prefix + idx;
			idx++;
		} while (ProcessNodeEditor.containsEdgeId(connections, id));
		return id;
	}

	/**
	 * @param {PConnection[]} connections
	 * @param {string} edgeId
	 * @return {boolean}
	 */
	static containsEdgeId(connections, edgeId) {
		for (let c of connections) {
			if (c.edge.id === edgeId) return true;
		}
		return false;
	}

	static cleanUpCoords(node, coords, defaults) {
		if (typeof coords.x !== 'number') coords.x = defaults.x;
		if (typeof coords.y !== 'number') {
			coords.y = defaults.y;
			defaults.y += 80;
		}

		if (typeof coords.w !== 'number' || typeof coords.h !== 'number' || coords.w < 10 || coords.h < 10) {
			if (typeof coords.w !== 'number') coords.w = defaults.w;
			if (typeof coords.h !== 'number') coords.h = defaults.h;
		}
	}
}
